"use client";
import { cn } from "@/lib/utils";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import {
  IconBrandDiscordFilled,
  IconBrandX,
  IconMenu2,
  IconX,
} from "@tabler/icons-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import { buttonVariants } from "../ui/button/button";
import Logo from "./logo";

const NAV_ITEMS = [
  { label: "Home page", href: "/" },
  { label: "Launchpad", href: "/launchpad" },
  { label: "Meme Coins", href: "/explore" },
];

const SOCIALS_LINKS = [
  { icon: <IconBrandDiscordFilled stroke={2} />, href: "/" },
  { icon: <IconBrandX stroke={2} />, href: "/" },
];

const NavLinks = ({
  className,
  isActive,
  onClick,
}: {
  className?: string;
  isActive: (href: string) => boolean;
  onClick?: () => void;
}) => {
  return (
    <ul className={cn("flex items-center", className)}>
      {NAV_ITEMS.map((link) => (
        <li key={link.label}>
          <Link
            href={link.href}
            className={cn(
              buttonVariants({
                variant: isActive(link.href) ? "primary" : "default",
              })
            )}
            onClick={onClick}
          >
            <span className="z-10">{link.label}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
};

const SocialLinks = () => {
  return (
    <>
      {SOCIALS_LINKS.map((link, index) => (
        <a
          key={index}
          className="bg-gradient-to-b from-[#14121A] to-[#16151C] border border-[#16151C] p-3 rounded-full hover:from-blue-700 hover:to-transparent hover:scale-[99%] hover:opacity-90 transition-all duration-500"
          href={link.href}
        >
          {link.icon}
        </a>
      ))}
    </>
  );
};

export default function Header() {
  const pathname = usePathname();
  const isActive = (href: string) => pathname === href;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (isMenuOpen && event.key === "Escape") {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isMenuOpen]);

  return (
    <header className="mt-[32px] relative transition-all duration-300">
      <div className="flex items-center justify-between h-[44px] max-lg:hidden">
        <div className="flex item-center gap-[24px]">
          <Logo />
          <nav className="bg-foreground p-1 rounded-full border border-primaryborder">
            <NavLinks isActive={isActive} />
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <SocialLinks />
          <WalletMultiButton />
        </div>
      </div>

      <div className="hidden max-lg:flex items-center justify-between h-[44px]">
        <Logo />
        <div className="flex items-center gap-4">
          <WalletMultiButton />
          <button
            type="button"
            name="NavLinks Toggle"
            aria-label={"NavLinks Toggle"}
            onClick={toggleMenu}
            className="bg-gradient-to-b from-[#14121A] to-[#16151C] border border-[#16151C] p-3 rounded-full "
          >
            {!isMenuOpen ? <IconMenu2 stroke={2} /> : <IconX stroke={2} />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div
          ref={menuRef}
          className="hidden max-lg:block absolute top-16 right-0 w-full bg-background border border-primaryborder rounded-lg shadow-lg z-50"
        >
          <nav className="p-4">
            <NavLinks
              isActive={isActive}
              className="flex flex-col items-start justify-start gap-4"
              onClick={closeMenu}
            />
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-primaryborder">
              <SocialLinks />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
